import React, { useMemo, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Smartphone, Copy, Check, Loader2, ExternalLink, ScanLine, Settings as SettingsIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { formatINR, api, formatApiErrorDetail } from "../lib/api";

/**
 * Build a standard UPI deep link.
 * Spec: upi://pay?pa=<vpa>&pn=<name>&am=<amount>&cu=INR&tn=<note>&tr=<txn_ref>
 */
export function buildUpiUri({ vpa, payeeName, amount, note, txnRef }) {
  const params = new URLSearchParams();
  if (vpa) params.set("pa", vpa);
  if (payeeName) params.set("pn", payeeName);
  if (amount != null && amount !== "") params.set("am", String(amount));
  params.set("cu", "INR");
  if (note) params.set("tn", note);
  if (txnRef) params.set("tr", txnRef);
  return `upi://pay?${params.toString()}`;
}

/**
 * UPI QR dialog — for in-clinic payments.
 * Patient scans the QR with any UPI app (GPay/PhonePe/Paytm) and pays directly to the clinic.
 * Receptionist clicks "Mark as paid" once they see the money arrive in their bank app.
 *
 * Props:
 *   - open, onOpenChange
 *   - invoice: { invoice_id, amount, patient_name, status }
 *   - upiVpa: string (clinic's UPI ID)
 *   - upiPayeeName: string (display name shown in UPI app — e.g. clinic name)
 *   - onPaid?: (updatedInvoice) => void  — invoked after Mark Paid succeeds
 */
export default function UpiQrDialog({ open, onOpenChange, invoice, upiVpa, upiPayeeName, onPaid }) {
  const [copied, setCopied] = useState(false);
  const [marking, setMarking] = useState(false);

  const txnRef = useMemo(() => {
    if (!invoice?.invoice_id) return undefined;
    // UPI tr must be alphanumeric, <=35 chars
    return `INV${invoice.invoice_id.slice(-12).replace(/[^A-Za-z0-9]/g, "")}`;
  }, [invoice?.invoice_id]);

  const upiUri = useMemo(() => {
    if (!upiVpa || !invoice) return null;
    return buildUpiUri({
      vpa: upiVpa,
      payeeName: upiPayeeName || "Clinic",
      amount: invoice.amount,
      note: `Invoice ${invoice.invoice_id?.slice(-8) || ""} · ${invoice.patient_name || ""}`.trim(),
      txnRef,
    });
  }, [upiVpa, upiPayeeName, invoice, txnRef]);

  const copyVpa = () => {
    if (!upiVpa) return;
    navigator.clipboard?.writeText(upiVpa);
    setCopied(true);
    toast.success("UPI ID copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const handleMarkPaid = async () => {
    if (!invoice?.invoice_id) return;
    setMarking(true);
    try {
      const { data } = await api.put(`/invoices/${invoice.invoice_id}/status`, {
        status: "paid",
        payment_method: "upi",
      });
      toast.success(`Paid · ${formatINR(invoice.amount || 0)} via UPI`);
      onPaid && onPaid(data || { ...invoice, status: "paid", payment_method: "upi" });
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to mark paid");
    } finally {
      setMarking(false);
    }
  };

  const hasVpa = Boolean(upiVpa && upiVpa.includes("@"));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="upi-qr-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <ScanLine className="w-5 h-5 text-violet-600" /> Scan to pay via UPI
          </DialogTitle>
          <DialogDescription>
            {invoice?.patient_name ? `${invoice.patient_name} · ` : ""}
            {invoice?.amount != null ? formatINR(invoice.amount) : ""}
            {invoice?.invoice_id && (
              <span className="text-slate-400"> · #{invoice.invoice_id.slice(-8)}</span>
            )}
          </DialogDescription>
        </DialogHeader>

        {!hasVpa ? (
          <div className="py-8 text-center space-y-4" data-testid="upi-not-configured">
            <div className="w-14 h-14 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mx-auto">
              <SettingsIcon className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <div className="font-semibold text-slate-900">UPI not configured</div>
              <p className="text-sm text-slate-500 mt-1 max-w-xs mx-auto">
                Add your clinic&apos;s UPI ID in Settings to start collecting payments instantly with a QR code.
              </p>
            </div>
            <Link to="/settings">
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => onOpenChange(false)} data-testid="upi-go-to-settings">
                Open Settings
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {/* QR */}
            <div className="flex justify-center">
              <div className="p-4 rounded-2xl bg-white border-2 border-slate-200 shadow-sm" data-testid="upi-qr-canvas-wrap">
                <QRCodeCanvas
                  value={upiUri}
                  size={220}
                  level="M"
                  includeMargin={false}
                  bgColor="#FFFFFF"
                  fgColor="#0F172A"
                />
              </div>
            </div>

            {/* VPA + amount summary */}
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Pay to</div>
                  <div className="text-sm font-semibold text-slate-900 truncate" data-testid="upi-payee-name">
                    {upiPayeeName || "Clinic"}
                  </div>
                  <div className="text-xs font-mono text-slate-600 truncate" data-testid="upi-vpa-text">{upiVpa}</div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={copyVpa}
                  data-testid="upi-copy-vpa-btn"
                  className="shrink-0"
                >
                  {copied ? <Check className="w-3.5 h-3.5 mr-1 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                <span className="text-xs text-slate-500">Amount</span>
                <span className="text-base font-bold text-slate-900" data-testid="upi-amount-text">
                  {formatINR(invoice?.amount || 0)}
                </span>
              </div>
            </div>

            {/* Open in UPI app (mobile only really) */}
            <a
              href={upiUri}
              target="_blank"
              rel="noopener noreferrer"
              className="block sm:hidden"
              data-testid="upi-open-app-link"
            >
              <Button variant="outline" className="w-full">
                <Smartphone className="w-4 h-4 mr-2" /> Open in UPI app
                <ExternalLink className="w-3 h-3 ml-1.5 text-slate-400" />
              </Button>
            </a>

            <div className="text-[11px] text-slate-500 leading-relaxed text-center">
              Patient scans this QR from any UPI app (GPay, PhonePe, Paytm) and pays directly into your bank account.
              <br />
              <span className="text-slate-400">No platform fees · Direct bank settlement</span>
            </div>
          </div>
        )}

        {hasVpa && invoice?.status !== "paid" && (
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="upi-cancel-btn">
              Close
            </Button>
            <Button
              onClick={handleMarkPaid}
              disabled={marking}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="upi-mark-paid-btn"
            >
              {marking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              I&apos;ve received payment
            </Button>
          </DialogFooter>
        )}
        {hasVpa && invoice?.status === "paid" && (
          <div className="flex justify-center pt-2">
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Already paid</Badge>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
